import { rest, stdParamsSerializer } from "src/common/rest";
import { apiMsObjectUrl, apiObjUrl } from "src/common/apiUrl";
import { ZUid } from "src/types/zUid";
import { zEntity, ZEntity } from "src/types/ZEntity";
import { zPlanning2Settings, ZPlanning2Settings } from "./ZPlanning2Settings";
import { zPlanning2Table, ZPlanning2Table } from "./ZPlanning2Row";
import {
  zPlanning2TableSettings,
  ZPlanning2TableSettings,
} from "./ZPlanning2TableSettings";

const urlPlanning2 = (path: string) => apiMsObjectUrl(`/planning${path}`);

export const loadPlanning2Settings = async (
  objectServiceId: ZUid,
): Promise<ZPlanning2Settings> => {
  const url = urlPlanning2(`/settings/${objectServiceId}`);
  const resp = await rest.get(url);
  return zPlanning2Settings.parse(resp.data);
};

export const savePlanning2Settings = async (
  objectServiceId: ZUid,
  settings: ZPlanning2Settings,
) => {
  const url = urlPlanning2(`/settings/${objectServiceId}`);
  const data = {
    ...settings,
    objectServiceId,
  };
  await rest.post(url, data);
};

export const loadPlanning2Stub = async (
  _o: ZUid,
  _e: ZUid,
): Promise<ZPlanning2Table> => ({
  startDate: "2026-08-01",
  endDate: "2026-08-20",
  tasks: [
    {
      kind: "task",
      id: "1",
      name: "Stub Task 1",
      duration: 3,
      actualStartDate: "2026-07-31",
      actualEndDate: "2026-08-02",
      plannedStartDate: "2026-08-01",
      plannedEndDate: "2026-08-03", // начало + длительность − 1.
    },
    {
      kind: "task",
      id: "2",
      name: "Sub Task 2",
      duration: 4,
      plannedStartDate: "2026-08-04",
      plannedEndDate: "2026-08-07", // 4+4-1
    },
  ],
});

export const loadPlanning2 = async (
  objectServiceId: ZUid,
  entityId: ZUid,
): Promise<ZPlanning2Table> => {
  const url = urlPlanning2(`/planinfo/${objectServiceId}/${entityId}`);
  // С бэка приходят данные, требующие коррекции прежде чем их можно парсить
  type Row = {
    id: string | null;
    children?: { kind?: string }[] | null;
    kind?: string;
    name: string;
  };
  const { data } = await rest.get<{ tasks: Row[] }>(url);
  const names: Record<string, number> = {};
  const tasks = Array.isArray(data.tasks)
    ? data.tasks.map((row) => {
        const newRow = { ...row };
        // Бэк не присылает id для групп. Но он нужен для таблицы. Поэтому генерируем его самостоятельно
        // ИД должен быть не случайным, а воспроизводимым. Иначе будет теряться состояние при перезагрузках после изменения таблицы.
        if (!row.id) {
          const { name } = row;
          if (typeof name !== "string") throw Error("Требуется значение name");
          if (names[name]) throw Error(`Дублируется имя группы '${name}'`);
          names[name] = 1;
          newRow.id = `@${name}`;
        }
        if (newRow.children) {
          newRow.kind = "group";
          newRow.children = newRow.children.map((it) => ({
            ...it,
            kind: "task",
          }));
        } else {
          newRow.kind = "task";
        }
        return newRow;
      })
    : data.tasks;

  return zPlanning2Table.parse({ ...data, tasks });
};

export const createPlanning2Task = async (
  objectServiceId: ZUid,
  entityId: ZUid,
  entity: ZEntity,
): Promise<ZEntity> => {
  const { id, ...body } = entity;
  const url = urlPlanning2(`/task/createTask/${objectServiceId}/${entityId}`);
  const resp = await rest.post(url, body);
  return zEntity.parse(resp.data);
};

export const updatePlanning2Task = async (
  objectServiceId: ZUid,
  entity: ZEntity,
): Promise<ZEntity> => {
  const url = urlPlanning2(`/task/partial/${objectServiceId}`);
  const resp = await rest.put(url, entity, {
    params: { withLabels: true },
  });
  return zEntity.parse(resp.data);
};

export const createPlanning2TaskFromTemplate = async (params: {
  templateEntityId: ZUid;
  objectServiceId: ZUid;
  entityId: ZUid;
}): Promise<void> => {
  const url = urlPlanning2("/task/createTaskFromTemplate");
  await rest.post(url, params);
};

export const deletePlanning2Tasks = async (ids: string[]): Promise<void> => {
  const url = urlPlanning2("/deleteTasks");
  await rest.delete(url, {
    params: { ids },
    paramsSerializer: stdParamsSerializer,
  });
};

export const calculatePlanning2 = async (
  objectServiceId: ZUid,
  entityId: ZUid,
  planDate: string,
) => {
  const url = urlPlanning2("/plan/calculate");
  await rest.post(url, { objectServiceId, entityId, planDate });
};

// table settings

const tableSettingsKey = "Planning2Table";
const urlPlanning2TableSettings = () =>
  apiObjUrl(`/user-preferences/${tableSettingsKey}`);

export const loadPlanning2TableSettings =
  async (): Promise<ZPlanning2TableSettings> => {
    const { data } = await rest.get(urlPlanning2TableSettings());
    if (!data) return {};
    return zPlanning2TableSettings.parse(data);
  };

export const savePlanning2TableSettings = async (
  settings: ZPlanning2TableSettings,
): Promise<void> => {
  await rest.post(urlPlanning2TableSettings(), settings);
};
